import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
	Button,
	Col,
	DatePicker,
	Form,
	Input,
	Modal,
	Radio,
	Row,
	Select,
	Spin,
	Typography,
	notification,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { api } from "../utils/Api";
import {
	filterOption,
	getDisabledDate,
	getDisabledTime,
	getAvailableOptions,
	useShiftGroupOptions,
} from "./shiftGroupUtils";
const { Text } = Typography;

const DriverShiftGroupNewModal = ({
	open,
	onClose,
	onSuccess,
	shift,
	plant,
	usedDrivers = [],
	usedTrucks = [],
}) => {
	const [form] = Form.useForm();
	const members = Form.useWatch("members", form);
	const [loading, setLoading] = useState(false);
	const [apiNotification, contextHolder] = notification.useNotification();

	const shiftDate = shift ? dayjs(shift.rawDate) : null;

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	const { driverOptions, truckOptions, loadingDrivers, loadingTrucks } =
		useShiftGroupOptions(open, plant, openNotification);

	const [defaultGroups, setDefaultGroups] = useState([]);
	const [loadingDefaultGroups, setLoadingDefaultGroups] = useState(false);
	const [selectedDefaultGroup, setSelectedDefaultGroup] = useState(null);

	useEffect(() => {
		setSelectedDefaultGroup(null);
		if (open && plant) {
			getDefaultGroups();
		}
	}, [open, plant]);

	// Default groups (ShiftGroupMember) of the plant
	const getDefaultGroups = async () => {
		const url = api + "/shift-group-member/list";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoadingDefaultGroups(true);
			const { data } = await axios.get(url, { params: { plant: plant } });
			if (data.success) {
				setDefaultGroups(data.payload);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to get default groups");
		} finally {
			setLoadingDefaultGroups(false);
		}
	};

	// Sets the name of the default group and replaces the list with its drivers. Shift start, shift end
	// and truck stay empty. Drivers already in other groups of this shift or no longer
	// active are skipped.
	const applyDefaultGroup = (groupId) => {
		const defaultGroup = defaultGroups.find((item) => item._id === groupId);
		setSelectedDefaultGroup(groupId);

		if (!defaultGroup) {
			return;
		}

		const activeDrivers = new Set(driverOptions.map((option) => option.value));
		const unavailable = new Set(usedDrivers);
		const added = defaultGroup.members.filter(
			(member) => activeDrivers.has(member._id) && !unavailable.has(member._id),
		);
		const skipped = defaultGroup.members.filter(
			(member) => !added.includes(member),
		);

		form.setFieldsValue({
			name: defaultGroup.name,
			members:
				added.length > 0 ? added.map((member) => ({ driver: member._id })) : [{}],
		});

		if (skipped.length > 0) {
			openNotification(
				"warning",
				"Some drivers were not added",
				`Already in another group of this shift or inactive: ${skipped
					.map((member) => member.name)
					.join(", ")}`,
			);
		}
	};

	// Values already used in other groups of this shift or in another row of this form
	const getRowOptions = (rowName, field, options, usedValues) => {
		const selectedInOtherRows = (members || [])
			.filter((member, index) => index !== rowName && member?.[field])
			.map((member) => member[field]);
		return getAvailableOptions(options, [...usedValues, ...selectedInOtherRows]);
	};

	const onFinish = async (values) => {
		const url = api + "/shift-group/create";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoading(true);
			const { data } = await axios.post(url, {
				name: values.name,
				shift: shift.id,
				members: values.members.map((member) => ({
					startDate: member.startDate.toDate(),
					endDate: member.endDate.toDate(),
					driver: member.driver,
					truck: member.truck,
				})),
			});
			if (data.success) {
				form.resetFields();
				onSuccess(data.message);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification(
				"error",
				"Error",
				"Error while trying to create shift group",
			);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			title="Create Group"
			open={open}
			onCancel={onClose}
			centered
			width={1000}
			destroyOnClose
			maskClosable={false}
			footer={[
				<Button key="cancel" size="large" onClick={onClose} disabled={loading}>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					size="large"
					loading={loading}
					onClick={() => form.submit()}
				>
					Save
				</Button>,
			]}
		>
			{contextHolder}
			<div style={{ marginTop: "20px" }}>
				<Text strong style={{ display: "block", marginBottom: 8 }}>
					Default Groups
				</Text>
				{loadingDefaultGroups || loadingDrivers ? (
					<Spin />
				) : defaultGroups.length === 0 ? (
					<Text type="secondary">There are no default groups for this plant</Text>
				) : (
					<Radio.Group
						optionType="button"
						buttonStyle="solid"
						value={selectedDefaultGroup}
						onChange={(e) => applyDefaultGroup(e.target.value)}
						options={defaultGroups.map((item) => ({
							label: item.name,
							value: item._id,
						}))}
					/>
				)}
			</div>
			<Form
				form={form}
				layout="vertical"
				style={{ marginTop: "20px" }}
				onFinish={onFinish}
				preserve={false}
			>
				<Form.Item
					name="name"
					label="Name"
					rules={[{ required: true, whitespace: true, message: "Name is required" }]}
				>
					<Input size="large" placeholder="Group name" />
				</Form.Item>
				<Form.List
					name="members"
					initialValue={[{}]}
					rules={[
						{
							validator: (_, members) =>
								members && members.length > 0
									? Promise.resolve()
									: Promise.reject(new Error("Add at least one driver")),
						},
					]}
				>
					{(fields, { add, remove }, { errors }) => (
						<>
							{fields.map(({ key, name, ...restField }) => (
								<Row key={key} gutter={16} align="middle">
									<Col span={24} md={12} lg={6}>
										<Form.Item
											{...restField}
											name={[name, "startDate"]}
											label="Shift Start"
											rules={[
												{ required: true, message: "Shift start is required" },
												{
													validator: (_, value) =>
														!value || !shiftDate || !value.isBefore(shiftDate, "minute")
															? Promise.resolve()
															: Promise.reject(
																	new Error(
																		"Shift start can't be earlier than the shift date",
																	),
															  ),
												},
											]}
										>
											<DatePicker
												size="large"
												showTime={{ format: "hh:mm A" }}
												format="MM/DD/YYYY hh:mm A"
												disabledDate={getDisabledDate(shiftDate)}
												disabledTime={getDisabledTime(shiftDate, false)}
												onChange={() =>
													form.validateFields([["members", name, "endDate"]]).catch(() => {})
												}
												style={{ width: "100%" }}
											/>
										</Form.Item>
									</Col>
									<Col span={24} md={12} lg={6}>
										<Form.Item noStyle dependencies={[["members", name, "startDate"]]}>
											{({ getFieldValue }) => {
												const startDate = getFieldValue(["members", name, "startDate"]);
												return (
													<Form.Item
														{...restField}
														name={[name, "endDate"]}
														label="Shift End"
														rules={[
															{ required: true, message: "Shift end is required" },
															{
																validator: (_, value) =>
																	!value ||
																	!startDate ||
																	value.isAfter(startDate, "minute")
																		? Promise.resolve()
																		: Promise.reject(
																				new Error(
																					"Shift end must be later than shift start",
																				),
																		  ),
															},
														]}
													>
														<DatePicker
															size="large"
															showTime={{ format: "hh:mm A" }}
															format="MM/DD/YYYY hh:mm A"
															disabled={!startDate}
															disabledDate={getDisabledDate(startDate)}
															disabledTime={getDisabledTime(startDate, true)}
															style={{ width: "100%" }}
														/>
													</Form.Item>
												);
											}}
										</Form.Item>
									</Col>
									<Col span={24} md={12} lg={5}>
										<Form.Item
											{...restField}
											name={[name, "driver"]}
											label="Driver"
											rules={[{ required: true, message: "Driver is required" }]}
										>
											<Select
												showSearch
												placeholder="Select driver"
												size="large"
												options={getRowOptions(name, "driver", driverOptions, usedDrivers)}
												filterOption={filterOption}
												loading={loadingDrivers}
												disabled={loadingDrivers}
											/>
										</Form.Item>
									</Col>
									<Col span={22} md={11} lg={5}>
										<Form.Item
											{...restField}
											name={[name, "truck"]}
											label="Truck"
											rules={[{ required: true, message: "Truck is required" }]}
										>
											<Select
												showSearch
												placeholder="Select truck"
												size="large"
												options={getRowOptions(name, "truck", truckOptions, usedTrucks)}
												filterOption={filterOption}
												loading={loadingTrucks}
												disabled={loadingTrucks}
											/>
										</Form.Item>
									</Col>
									<Col span={2} md={1} lg={2}>
										{fields.length > 1 && (
											<MinusCircleOutlined
												style={{ fontSize: 20, marginTop: 8 }}
												onClick={() => remove(name)}
											/>
										)}
									</Col>
								</Row>
							))}
							<Form.Item>
								<Button
									type="dashed"
									size="large"
									onClick={() => add()}
									icon={<PlusOutlined />}
									block
								>
									Add Driver
								</Button>
								<Form.ErrorList errors={errors} />
							</Form.Item>
						</>
					)}
				</Form.List>
			</Form>
		</Modal>
	);
};

export default DriverShiftGroupNewModal;
